import React, { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import { Link } from "react-router-dom";
import { X, Settings, Cookie } from "lucide-react";

const COOKIE_CONSENT_KEY = "medica_cookie_consent";
const COOKIE_PREFERENCES_KEY = "medica_cookie_preferences";

export default function CookiePolicyWindow() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Always true, cannot be disabled
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    // Small delay to ensure page loads first
    const timer = setTimeout(() => {
      const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
      if (!consent) {
        setVisible(true);
      } else {
        // Load saved preferences
        const savedPrefs = localStorage.getItem(COOKIE_PREFERENCES_KEY);
        if (savedPrefs) {
          setPreferences(JSON.parse(savedPrefs));
        }
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const savePreferences = (prefs) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, "true");
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(prefs));
    localStorage.setItem("consent_timestamp", new Date().toISOString());
    
    // Apply preferences (you can extend this to actually enable/disable tracking)
    if (prefs.analytics) {
      // Enable analytics
      console.log("Analytics enabled");
    }
    if (prefs.marketing) {
      // Enable marketing cookies
      console.log("Marketing enabled");
    }
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      essential: true,
      analytics: true,
      marketing: true,
    };
    setPreferences(allAccepted);
    savePreferences(allAccepted);
    setVisible(false);
    setShowSettings(false);
  };

  const handleRejectAll = () => {
    const essentialOnly = {
      essential: true,
      analytics: false,
      marketing: false,
    };
    setPreferences(essentialOnly);
    savePreferences(essentialOnly);
    setVisible(false);
    setShowSettings(false);
  };

  const handleSavePreferences = () => {
    savePreferences(preferences);
    setVisible(false);
    setShowSettings(false);
  };

  const togglePreference = (key) => {
    if (key === "essential") return; // Cannot disable essential cookies
    setPreferences((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  if (!visible) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/20 backdrop-blur-sm z-[9998] animate-fadeIn"
        onClick={() => !showSettings && setVisible(false)}
      />

      {/* Cookie Banner */}
      <div
        className={`fixed bottom-0 left-0 right-0 md:bottom-4 md:left-4 md:right-auto z-[9999] shadow-2xl rounded-t-2xl md:rounded-2xl p-6 md:max-w-md bg-white border border-gray-200 transition-all duration-500 ${
          visible ? 'animate-slideUp md:animate-slideInLeft' : ''
        }`}
        role="dialog"
        aria-labelledby="cookie-policy-title"
        aria-describedby="cookie-policy-description"
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-2">
            <Cookie className="w-6 h-6 text-[#c8102e]" />
            <h2 id="cookie-policy-title" className="text-lg font-bold text-gray-900">
              {t('cookie_preferences_title')}
            </h2>
          </div>
          <button
            onClick={() => setVisible(false)}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100"
            aria-label="Close cookie banner"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {!showSettings ? (
          <>
            {/* Description */}
            <p id="cookie-policy-description" className="mb-4 text-sm text-gray-700 leading-relaxed">
              {t('cookie_preferences_desc')}{' '}
              <Link
                to="/cookie-policy"
                className="underline text-[#c8102e] hover:text-[#8b0000] font-medium focus:outline-none focus:ring-2 focus:ring-[#c8102e] focus:ring-offset-1 rounded"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('cookie_preferences_learn_more')}
              </Link>
            </p>

            {/* Action Buttons */}
            <div className="flex flex-col gap-2">
              <button
                onClick={handleAcceptAll}
                className="w-full px-4 py-3 font-semibold text-white bg-[#c8102e] rounded-lg hover:bg-[#a00d24] focus:outline-none focus:ring-2 focus:ring-[#c8102e] focus:ring-offset-2 transition-all duration-200 shadow-md hover:shadow-lg"
                aria-label="Accept all cookies"
              >
                {t('cookie_preferences_accept_all')}
              </button>
              
              <div className="flex gap-2">
                <button
                  onClick={handleRejectAll}
                  className="flex-1 px-4 py-2.5 font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 transition-all duration-200"
                  aria-label="Reject non-essential cookies"
                >
                  {t('cookie_preferences_reject_all')}
                </button>
                
                <button
                  onClick={() => setShowSettings(true)}
                  className="flex-1 px-4 py-2.5 font-medium text-[#c8102e] border-2 border-[#c8102e] rounded-lg hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-[#c8102e] focus:ring-offset-2 transition-all duration-200 flex items-center justify-center gap-2"
                  aria-label="Customize cookie preferences"
                >
                  <Settings className="w-4 h-4" />
                  {t('cookie_preferences_customize')}
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Settings View */}
            <div className="space-y-4 mb-4">
              {/* Essential Cookies */}
              <div className="flex items-start justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-sm text-gray-900">{t('cookie_essential_title')}</h3>
                    <span className="text-xs bg-gray-200 text-gray-700 px-2 py-0.5 rounded-full">{t('cookie_essential_badge')}</span>
                  </div>
                  <p className="text-xs text-gray-600">
                    {t('cookie_essential_desc')}
                  </p>
                </div>
                <div className="ml-3">
                  <input
                    type="checkbox"
                    checked={true}
                    disabled
                    className="w-5 h-5 rounded border-gray-300 text-[#c8102e] focus:ring-[#c8102e] cursor-not-allowed opacity-50"
                  />
                </div>
              </div>

              {/* Analytics Cookies */}
              <div className="flex items-start justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <h3 className="font-semibold text-sm text-gray-900 mb-1">{t('cookie_analytics_title')}</h3>
                  <p className="text-xs text-gray-600">
                    {t('cookie_analytics_desc')}
                  </p>
                </div>
                <div className="ml-3">
                  <input
                    type="checkbox"
                    checked={preferences.analytics}
                    onChange={() => togglePreference("analytics")}
                    className="w-5 h-5 rounded border-gray-300 text-[#c8102e] focus:ring-[#c8102e] cursor-pointer"
                    aria-label="Toggle analytics cookies"
                  />
                </div>
              </div>

              {/* Marketing Cookies */}
              <div className="flex items-start justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <h3 className="font-semibold text-sm text-gray-900 mb-1">{t('cookie_marketing_title')}</h3>
                  <p className="text-xs text-gray-600">
                    {t('cookie_marketing_desc')}
                  </p>
                </div>
                <div className="ml-3">
                  <input
                    type="checkbox"
                    checked={preferences.marketing}
                    onChange={() => togglePreference("marketing")}
                    className="w-5 h-5 rounded border-gray-300 text-[#c8102e] focus:ring-[#c8102e] cursor-pointer"
                    aria-label="Toggle marketing cookies"
                  />
                </div>
              </div>
            </div>

            {/* Settings Action Buttons */}
            <div className="flex gap-2">
              <button
                onClick={() => setShowSettings(false)}
                className="flex-1 px-4 py-2.5 font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-400 transition-all duration-200"
              >
                {t('cookie_back_button')}
              </button>
              <button
                onClick={handleSavePreferences}
                className="flex-1 px-4 py-3 font-semibold text-white bg-[#c8102e] rounded-lg hover:bg-[#a00d24] focus:outline-none focus:ring-2 focus:ring-[#c8102e] focus:ring-offset-2 transition-all duration-200 shadow-md"
              >
                {t('cookie_save_preferences')}
              </button>
            </div>
          </>
        )}

        {/* Footer Note */}
        <p className="mt-4 text-xs text-gray-500 text-center">
          {t('cookie_footer_note')}{' '}
          <Link to="/cookie-policy" className="text-[#c8102e] hover:underline">
            {t('cookie_footer_link')}
          </Link>
        </p>
      </div>
    </>
  );
}


